// MarkdownBody.jsx — 第八十八批：ticket / 工作项正文的唯一展示件。
//
// ── 一只渲染器，两个调用点 ──────────────────────────────────────────────────
// 渲染用的是 `scripts/publish/md-render.mjs` ——**发布管线用的那一只，逐字同一份**。
// 它是纯字符串函数、零 import，所以 esbuild 直接打进包里。
// 另写一只前端渲染器是显而易见的第二选择，也是显而易见的错误：两只渲染器意味着
// 同一段 markdown 在文档页与工单页可以长得不一样，而那种差异没有任何判据看得住。
//
// ── 为什么敢把用户提交的文本喂给它 ──────────────────────────────────────────
//   · 裸 HTML 一直是**转义**的（`<img onerror=…>` 变成字面文字），这是它从 SB-015
//     起就写在头注里的承诺；
//   · 链接协议白名单是本批补的（`javascript:` / `data:` 一律不出 href）——那是渲染
//     用户输入之前唯一缺的一道；
//   · 标题不出 id（headingIds:false）：同一屏上会挂着好几段正文，per-call 的去重
//     管不了跨段重名。
//   · **图片整个不渲染（images:false，第一百零九批处置轮 R2）**：一条链接要人点，
//     一张图片是**打开页面就自动发出、带着读者自己 cookie 的一次 GET**。而本仓有两条
//     会改状态的 GET（密递取件即焚、站内信点开即已读），站内信正文又完全由发送方写、
//     bodyFormat 也由他选。`![](/api/secret-deliveries/123)` 是相对 URL，协议白名单
//     按设计放行它——于是受害者一打开那封信，指名给他的密件会被逐个焚掉（而本仓的
//     语义是「taken ⇒ 凭据可能已外流，去轮换」，一次纯破坏被读成一次泄漏事件）。
//     端点侧另有 `Sec-Fetch-Dest` 那道闸兜底，两道各自独立、缺一不可。
//     代价是这些正文里贴不了图——而这条通道本来也没有图床，附件走的是工单附件面。
//
// ── pre-wrap 落在 `& p` 上，不落在容器上 ────────────────────────────────────
// 渲染器在**块与块之间**也吐 `\n`。把 pre-wrap 加在容器上会让那些 `\n` 一起变成
// 可见空行，于是每两个块之间凭空多一行。落在 `& p` 上才只作用于段内的软换行——
// 那正是我们要保住的东西：纯文本正文的换行观感与今天逐字一致。
import { useState } from 'react';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { renderMarkdown } from '../../../../scripts/publish/md-render.mjs';
import { shouldRenderMarkdown } from './bodyFormat.mjs';
import { useT } from '../i18n/index.mjs';

export default function MarkdownBody({ body, format, emptyText = '—' }) {
  const t = useT();
  // 读者侧的出口。存量 ticket 没有任何写路径（网关里没有 ticket 的 PATCH/PUT），
  // 一条被渲染坏的老正文只能从这里救。
  const [showSource, setShowSource] = useState(false);

  if (!body) return <Typography variant="body2">{emptyText}</Typography>;

  if (!shouldRenderMarkdown(format, showSource)) {
    return (
      <>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {body}
        </Typography>
        {showSource ? (
          <MuiLink component="button" type="button" variant="caption" underline="hover"
            onClick={() => setShowSource(false)} sx={{ mt: 0.5, display: 'block' }}>
            {t('body.showRendered')}
          </MuiLink>
        ) : null}
      </>
    );
  }

  // 渲染器已转义一切原始 HTML，并且本批起对链接协议设了白名单（见头注）。
  // `images:false` 是**这一只调用点**独有的一条（发布管线照常出图）：见头注末节。
  const { html } = renderMarkdown(body, { headingIds: false, images: false });
  return (
    <>
      <Box
        dangerouslySetInnerHTML={{ __html: html }}
        sx={{
          fontSize: 14, lineHeight: 1.7, wordBreak: 'break-word',
          // 段内软换行保住；块间的 \n 不受影响（见头注）。
          '& p': { whiteSpace: 'pre-wrap', my: 0.75 },
          '& p:first-of-type': { mt: 0 },
          '& p:last-child': { mb: 0 },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            fontSize: 15, fontWeight: 700, mt: 1.5, mb: 0.5,
          },
          '& ul, & ol': { pl: 3, my: 0.75 },
          '& li': { mb: 0.25 },
          '& code': {
            fontFamily: 'monospace', fontSize: '0.9em',
            bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5,
          },
          '& pre': {
            bgcolor: 'action.hover', p: 1, borderRadius: 1, overflowX: 'auto', my: 1,
          },
          '& pre code': { bgcolor: 'transparent', px: 0 },
          '& blockquote': {
            borderLeft: 3, borderColor: 'divider', pl: 1.5, ml: 0, my: 1,
            color: 'text.secondary',
          },
          // 宽表格必须自己滚，页面本体不许横向滚。
          '& table': { borderCollapse: 'collapse', my: 1, display: 'block', overflowX: 'auto' },
          '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5 },
          '& img': { maxWidth: '100%' },
          '& hr': { border: 0, borderTop: 1, borderColor: 'divider', my: 1.5 },
        }}
      />
      <MuiLink component="button" type="button" variant="caption" underline="hover"
        onClick={() => setShowSource(true)} sx={{ mt: 0.5, display: 'block' }}>
        {t('body.showSource')}
      </MuiLink>
    </>
  );
}
